import Image from 'next/legacy/image'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'

import FaqAccordion, { ItemLink } from './faq-accordion'
import landlordTransactionImage from './images/landlord-transaction-summary.png'

const ImageWrapper = styled.div`
  padding: 20px 0;
  display: flex;
  justify-content: center;
`

const Landlords = () => {
  const { t } = useTranslation(['faq'])

  const LANDLORD_TIPS = [
    {
      title: t('faq:How do you advertise a rental property?'),
      path: 'how-to-advertise-rental-property',
      answers: [
        {
          steps: [
            <Trans
              key="Using liv.rent is the best way to get your rental property seen by quality tenants faster."
              i18nKey="faq:Using liv.rent is the best way to get your rental property seen by quality tenants faster."
            >
              Using{' '}
              <ItemLink
                href="https://liv.rent/"
                rel="noopener noreferrer"
                target="_blank"
              >
                liv.rent
              </ItemLink>{' '}
              is the best way to get your rental property seen by quality
              tenants faster. Here’s how landlords and property managers can get
              their listing advertised for free:
            </Trans>
          ]
        },
        {
          heading: t('faq:Advertise online'),
          steps: [
            t(
              'faq:The best way to advertise your rental property is online. Although it is common to post on traditional classified sites, you are much better off posting on a dedicated rental property listing site such as '
            ),
            {
              text: t('faq:liv.rent'),
              href: 'https://liv.rent/landlords'
            },
            t('faq:. With a built-in '),
            {
              text: t('faq:verification process'),
              href: 'https://liv.rent/blog/2019/10/verification-process/'
            },
            t(
              'faq:, you have access to a pool of pre-screened tenants. Plus, you can evaluate the credibility of potential tenants based on their '
            ),
            {
              text: t('faq:Trust Score'),
              href: 'https://liv.rent/blog/2019/10/trust-score-explained/'
            },
            t(
              'faq: our intelligent rating system powered by an algorithm that corroborates submitted tenant information.'
            )
          ]
        },
        {
          heading: t('faq:Promote through social media'),
          steps: [
            {
              text: 'Marketing rental apartments',
              href: 'https://liv.rent/blog/2019/04/using-social-media-to-market-your-rental/'
            },
            t('faq: on social media is a must. For example, '),
            {
              text: t('faq:Facebook Rental Groups'),
              href: 'https://www.facebook.com/groups/livrentlistings/'
            },
            t(
              "faq: and Facebook Marketplace are excellent channels to increase exposure to your rental listing. Make sure that the link to your rental listing has been optimized for social sharing (i.e. your link contains a proper thumbnail image, title, and description). Certain rental property listing sites have syndicated channels that allow you to cross-promote. For example, you can easily share your rental listing to liv.rent's syndicated channels including Facebook Marketplace, Facebook Rental Groups, WeChat, as well as Craigslist, Kijiji, Zumper and more."
            )
          ]
        },
        {
          heading: t(
            'faq:Write an advertisement tailored to your target audience'
          ),
          steps: [
            t(
              'faq:Advertisement tailored to target audience',
              'Create a persona that reflects your ideal tenant, and identify key points that would attract this type of person. In your advertisement, make sure to highlight the unique features of your property in your introduction paragraph and provide the following information: location, amenities, utilities, pet policy, price, move-in date, lease type. Most importantly, always include '
            ),
            {
              text: t('faq:quality photos'),
              href: 'https://liv.rent/blog/2018/09/tips-for-photographing-your-rental-property/'
            }
          ]
        },
        {
          heading: t('faq:Showcase your rental property with video tours'),
          steps: [
            t('faq:Uploading '),
            {
              text: t('faq:rental listing video tours'),
              href: 'https://liv.rent/blog/2020/05/feature-update-view-rental-listings-online-with-video-tours/'
            },
            t(
              'faq: is another way to get seen. In addition to giving prospective tenants a better spatial understanding of your property, it also helps to reduce the number of in-person viewings required, freeing up more of your time.'
            )
          ]
        },
        {
          heading: t('faq:Price competitively'),
          steps: [
            t('faq:Pricing is key to attracting the '),
            {
              text: t('faq:right tenant'),
              href: 'https://liv.rent/blog/2018/10/how-to-pick-the-right-tenant'
            },
            t(
              "faq: and keeping your tenant turnover low. As a result, it's important to research beforehand and find out the average rental rate in the market. To save you time from having to do the research, liv.rent's "
            ),
            {
              text: t('faq:landlord dashboard'),
              href: 'https://liv.rent/blog/2020/10/landlord-dashboard-digital-tools-for-landlords-property-managers/'
            },
            t(
              'faq: will show you the average rent for a comparable property and how your pricing compares to similar listings.'
            )
          ]
        },
        {
          heading: t('faq:Advertise your property through offline promotion'),
          steps: [
            t(
              'faq:Depending on who your prospective tenants are, you can place a rental ad in newspapers. Other options include local bulletin boards (e.g. universities, grocery stores, community centers), word of mouth, and posting a "For Rent" sign out on your property.'
            )
          ]
        }
      ]
    },
    {
      title: t(
        'faq:What are common addendums that can be added to a rental agreement?'
      ),
      path: 'what-are-common-lease-addendums',
      answers: [
        {
          steps: [
            <Trans
              key="Changes to a tenancy agreement can only be made through an addendum"
              i18nKey="faq:Changes to a tenancy agreement can only be made through an addendum"
            >
              Changes to a{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/09/residential-tenancy-agreement-explained/"
                rel="noopener noreferrer"
                target="_blank"
              >
                tenancy agreement
              </ItemLink>{' '}
              can only be made through an addendum, which is a separate document
              added to the lease agreement outlining the modifications to the
              original terms. All changes must be in writing and be signed by
              both parties to make it legally binding.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="Common addendums include the following"
              i18nKey="faq:Common addendums include the following"
            >
              Common addendums include the following:
              <ul>
                <li>
                  Pets (e.g. allow pets when the original lease prohibited it)
                </li>
                <li>Smoking (e.g. add a smoke-free policy)</li>
                <li>
                  Sublet (e.g. give permission to sublet a rental unit or assign
                  a tenancy agreement){' '}
                  <ItemLink
                    href="https://liv.rent/blog/2019/09/residential-tenancy-agreement-explained/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Learn more
                  </ItemLink>
                </li>
                <li>
                  Roommates (e.g. adjust the rent in response to additional
                  occupants)
                </li>
                <li>Late Fee (e.g. set the late fee amount)</li>
                <li>
                  Extension (e.g. extend the end date of a lease agreement)
                </li>
              </ul>
              <ItemLink
                href=""
                rel="noopener noreferrer"
                target="_blank"
              ></ItemLink>
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="On liv.rent, you can easily add an addendum to your rental agreement"
              i18nKey="faq:On liv.rent, you can easily add an addendum to your rental agreement"
            >
              On liv.rent, you can easily add an addendum to your rental
              agreement. Vetted by industry experts, choose from an array of
              pre-set addendums that are customizable to suit your needs. Learn
              more about liv.rent&apos;s extensive{' '}
              <ItemLink
                href="https://liv.rent/landlords"
                rel="noopener noreferrer"
                target="_blank"
              >
                landlord features
              </ItemLink>
              .
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:What information can a landlord ask for?'),
      subtitle: t(
        'Landlords can ask for the following information from tenants:'
      ),
      path: 'what-information-can-i-ask',
      answers: [
        {
          heading: t('faq:ID'),
          steps: [
            t(
              "faq:Landlords are permitted to ask for the tenant's name and proof of identity. Example documents: Driver's License, Passport, Canadian Citizenship Card, Permanent Resident Card, or any other government-issued photo ID.Contact Information"
            )
          ]
        },
        {
          heading: t('faq:References'),
          steps: [
            t(
              "faq:Landlords have the right to inquire about the tenant's eviction history and ask for references. This includes current or previous landlords."
            )
          ]
        },
        {
          heading: t('Income and Employment Verification'),
          steps: [
            t(
              'faq:If a tenant is unable to provide an adequate reference, a landlord can request income and employment verification. Example documents: T4, Pay Stub, Employment Letter, Bank Statements, Post-dated Cheques'
            )
          ]
        },
        {
          steps: [
            {
              text: t('faq:Landlords in B.C.'),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/during-a-tenancy/'
            },
            t(
              'faq: are allowed to collect post-dated cheques from a tenant, but this is not the case in other Canadian provinces such as Quebec. In order to request post-dated cheques, it must be stated as a term in the agreement. Additionally, when the tenant moves out, any remaining cheques must be returned.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:The same documents can be requested from student tenants, but keep in mind that students may not be able to provide income verification. As an alternative, students are allowed to submit student loan financial statements. However, in most cases, landlords will request a guarantor (i.e. someone who would take over payments if the renter breaks the terms in the rental contract).'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:How much notice do you have to give to a tenant?'),
      path: 'how-much-notice-to-give',
      answers: [
        {
          steps: [
            t(
              'faq:Advance notice must be given if a landlord chooses to end a tenancy agreement early. However, the number of days of advance notice will vary depending on the situation.'
            )
          ]
        },
        {
          heading: t(
            "faq:Tenant doesn't pay the full rent or utilities on the agreed upon due date."
          ),
          steps: [
            t(
              'faq:A 10 Day Notice to End Tenancy can be served. If the 10 Day Notice ends the tenancy before the month ends, the tenant is still required to pay the landlord full rent amount for the month.'
            )
          ]
        },
        {
          heading: t('faq:Tenant breaches the residential tenancy agreement.'),
          steps: [
            t(
              'faq:One month notice to end tenancy',
              'Landlords can serve a One Month Notice to End Tenancy for the following reasons:'
            ),
            <ul key="One month notice reasons list">
              <li>
                {t(
                  'faq:Failure to pay the security or pet damage deposit within 30 days of entering a tenancy agreement'
                )}
              </li>
              <li>{t('faq:Repeatedly late rent payments (min. 3 times)')}</li>
              <li>
                {t(
                  'faq:Broke a material term *Material term is a term that both parties have agreed to and breach of such term allows the other party the right to end the agreement'
                )}
              </li>
              <li>
                {t(
                  "faq:Assigned or sublet a rental unit without the landlord's consent"
                )}
              </li>
              <li>
                <Trans i18nKey="faq:For a list of breaches, please visit gov.bc.ca">
                  For a full list of breaches, please visit{' '}
                  <ItemLink
                    href="https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/ending-a-tenancy/landlord-notice"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    gov.bc.ca
                  </ItemLink>
                  .
                </Trans>
              </li>
            </ul>
          ]
        },
        {
          heading: t('faq:Landlord decides to use the property.'),
          steps: [
            t(
              'faq:If a landlord decides to move back into the property, then a Two Month Notice to End Tenancy is required.'
            )
          ]
        },
        {
          heading: t(
            'faq:Landlord plans to do major construction or renovation.'
          ),
          steps: [
            t(
              'faq:For any major construction or extensive renovation which requires the unit to be empty, a landlord must serve a Four Month Notice to End Tenancy.'
            )
          ]
        },
        {
          steps: [
            <Trans
              key="Check out other common reasons for ending a tenancy agreement early and the proper notice to give at gov.bc.ca."
              i18nKey="faq:Check out other common reasons for ending a tenancy agreement early and the proper notice to give at gov.bc.ca."
            >
              Check out other common reasons for ending a tenancy agreement
              early and the proper notice to give at{' '}
              <ItemLink
                href="https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/ending-a-tenancy/landlord-notice"
                rel="noopener noreferrer"
                target="_blank"
              >
                gov.bc.ca
              </ItemLink>
              . And for a quick summary, read our{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/landlord-guide-end-extend-tenancy-agreement/"
                rel="noopener noreferrer"
                target="_blank"
              >
                blog post
              </ItemLink>{' '}
              on how to end or extend a tenancy agreement.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:How to evict a tenant?'),
      path: 'how-to-evict-tenant',
      answers: [
        {
          steps: [
            t(
              'faq:In order to evict a tenant, you must provide an acceptable reason and provide your tenant with an approved notice.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:Common reasons for eviction',
              'Common reasons for eviction include but are not limited to the following:'
            ),
            <ul key="Common eviction reasons list">
              <li>
                {t(
                  "faq:Tenant didn't pay security or pet deposit within 30 days of the date required under the tenancy agreement"
                )}
              </li>
              <li>{t('faq:Too many occupants in the rental unit')}</li>
              <li>
                {t(
                  'faq:Tenant disturbed or seriously jeopardized the health and/or safety of another occupant or the landlord'
                )}
              </li>
              <li>
                {t(
                  'faq:Tenant put the property at significant risk (e.g. illegal activity in the rental suite that caused property damage)'
                )}
              </li>
            </ul>
          ]
        },
        {
          steps: [
            t(
              'faq:For an extensive list of causes for eviction, visit Section 47 of the '
            ),
            {
              text: t('faq:Residential Tenancy Agreement (RTA)'),
              href: 'https://www.bclaws.ca/civix/document/id/complete/statreg/00_02078_01#section47'
            }
          ]
        },
        {
          steps: [
            t(
              'faq:After confirming the reason for eviction, you must give the right amount of notice to your tenant.'
            ),
            <br key="breakline" />,
            t(
              'faq:i) For non-payment of rent, provide a 10 Day Eviction Notice. This applies even if the tenant is a few dollars short or one day late.'
            ),
            <br key="breakline" />,
            t(
              'faq:ii) For a specific cause, provide a 30 Day Eviction Notice. Here is a '
            ),
            {
              text: t('faq:list of causes'),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/ending-a-tenancy/landlord-notice/one-month-notice'
            },
            t(
              'faq: that allows you to serve a One Month Notice To End Tenancy'
            ),
            <br key="breakline" />,
            t(
              'faq:iii) For your own use of property, you must issue a Two Month Eviction Notice.'
            ),
            <br key="breakline" />,
            t(
              'faq:iv) For any major construction or renovation, you need to provide a Four Month Eviction Notice. However, prior to issuing your eviction notice, you must have the necessary permits required by law.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:However, prior to issuing your eviction notice, you must have the necessary permits required by law. Once you have handed the written Notice of Eviction, your tenant must resolve the situation within the prescribed timeline.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:If resolved, the notice is cancelled and tenancy resumes. However, if your tenant does nothing, then you will need to get an Order of Possession (document to force your tenant to move out) and a Monetary Order (permission to recover unpaid rent and filing fee) through a '
            ),
            {
              text: t('faq:Direct Request'),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/apply-online/direct-request'
            }
          ]
        },
        {
          steps: [
            t('faq:Residential Tenancy Branch will review the application'),
            <ol key="Residential Tenancy Branch list">
              <li>
                {t(
                  'faq:You are granted an Order of Possession along with the monetary order, and can proceed with the eviction process.'
                )}
              </li>
              <li>
                {t(
                  'faq:You are scheduled for a participatory hearing, which you must attend with your tenant and provide supporting evidence for your case.'
                )}
              </li>
              <li>
                <Trans i18nKey="faq:Your application is dismissed and you may or may not be allowed to apply for dispute resolution.">
                  Your application is dismissed and you may or may not be
                  allowed to apply for{' '}
                  <ItemLink
                    href="https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/solving-problems/dispute-resolution"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    dispute resolution
                  </ItemLink>
                  .
                </Trans>
              </li>
            </ol>
          ]
        },
        {
          steps: [
            t('faq:>> Want to learn more? Check out our '),
            {
              text: t('faq:blog post'),
              href: 'https://liv.rent/blog/2019/11/causes-for-eviction/'
            },
            t(
              'faq: about the causes for eviction and types of eviction notice. '
            )
          ]
        }
      ]
    },
    {
      title: t('faq:What should I do if my tenant abandons the rental unit?'),
      path: 'what-if-tenant-abandons-unit',
      answers: [
        {
          steps: [
            t(
              'faq:A tenancy is considered abandoned',
              'A tenancy is considered abandoned when rent has not been paid for at least for a month and one of the following conditions applies:'
            ),
            <ul key="Abandoned tenancy list">
              <li>{t('faq:Tenant has removed their possessions')}</li>
              <li>
                {t(
                  "faq:Tenant stated that they don't intend to return or certain actions indicate that the tenant does not intend to return (e.g. moving to a care home)"
                )}
              </li>
            </ul>
          ]
        },
        {
          steps: [
            t(
              'faq:In the case that the tenant did abandon the rental unit, landlords can apply for dispute resolution to request compensation (e.g. unpaid rent or utilities and additional expenses). >> Here is a quick guide on '
            ),
            {
              text: t('faq:dispute resolution.'),
              href: 'https://liv.rent/blog/2019/12/how-to-dispute-resolution/'
            }
          ]
        }
      ]
    },
    {
      title: t('faq:Can landlords refuse pets?'),
      path: 'can-i-refuse-pets',
      answers: [
        {
          steps: [
            t(
              'faq:Depending on where you are located, landlords may or may not have the right to refuse pets. In Ontario, landlords cannot refuse a tenant who has a pet. According to Section 14 of the Ontario Residential Tenancy Act, leases cannot include a "no pets" provision. If such provision exists, then that section of the lease is void. The only exception is if the rental property is a condominium.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:For most provinces like Alberta, Manitoba, Quebec and B.C., landlords can refuse pets. The only exception is if a tenant requires a service animal due to a disability. Guide and service dogs are legally allowed to live in rental properties, and landlords are not allowed to request a pet damage deposit.'
            )
          ]
        },
        {
          steps: [
            t('faq:Pet policy update note'),
            <br key="breakline" />,
            t("faq:If you're wondering whether or not you should "),
            {
              text: t('faq:allow pets at your rental property'),
              href: 'https://liv.rent/blog/2018/11/should-you-allow-pets-in-your-rental/'
            },
            t(
              'faq:, learn more about the benefits of having a pet-friendly rental and what pet policies you should include in your tenancy agreement'
            )
          ]
        }
      ]
    },
    {
      title: t("faq:What to do if tenant doesn't pay rent?"),
      path: 'what-if-tenant-doesnt-pay-rent',
      answers: [
        {
          steps: [
            t(
              'faq:10 day notice',
              "If your tenant doesn't pay rent (i.e. the full amount and on-time) by midnight on the due date, then you can serve a 10 Day Notice to End Tenancy. [NOTE: This must be a written notice.]"
            )
          ]
        },
        {
          steps: [
            t(
              'faq:After receiving the notice, the tenant can either pay the overdue amount within five days or dispute it. If the tenant does nothing, then an alternate option is to apply for Landlord Direct Request. This allows landlords to claim for an '
            ),
            {
              text: t('faq:Order of Possession'),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/solving-problems/dispute-resolution/after-the-hearing/serving-and-enforcing-orders/enforcing-an-order-of-possession'
            },
            t(
              'faq: (a document that allows landlords to force a tenant to move out) and a monetary order (an order that allows landlords to recover unpaid rent and utilities & payment of the filing fee).'
            )
          ]
        },
        {
          steps: [
            t(
              "faq:If you don't have all the paperwork to complete the Direct Request process, then you can apply for dispute resolution. See what options are available if your tenant doesn't pay rent >> "
            ),
            {
              text: t('faq:Click here'),
              href: 'https://www2.gov.bc.ca/assets/gov/housing-and-tenancy/residential-tenancies/flow-charts/pe6.pdf'
            }
          ]
        }
      ]
    },
    {
      title: t('faq:Can I force my tenant to buy tenant insurance?'),
      path: 'force-tenants-to-buy-insurance',
      answers: [
        {
          steps: [
            t(
              "faq:Although tenant insurance is not mandated by law, landlords can require renters to have tenant insurance as part of the lease agreement. However, it's up to the tenant to agree with the term. If your tenant agrees to have insurance, then you have the right to require proof of that insurance whether it's on an annual basis or as a one-off. >> "
            ),
            {
              text: t('faq:Learn More'),
              href: 'https://www.landlordandtenant.org/leases-and-agreements/proof-of-insurance/'
            }
          ]
        }
      ]
    },
    {
      title: t(
        'faq:What can I do if my tenant has more people living in the suite?'
      ),
      path: 'what-to-do-if-too-many-occupants',
      answers: [
        {
          steps: [
            t(
              'faq:First of all, you need to know the difference between a guest and an occupant.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:If your tenant invited guests to stay over, then your only option is to have a term in the tenancy agreement specifying how long guests can stay. However, if a dispute arises, an arbitrator may side with the tenant and dismiss your claim. It\'s important to note that landlords can neither stop the tenant from having guests under "reasonable" circumstances nor charge a fee for guests.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:However, if you believe that a guest has become an occupant, you can issue a One Month Notice to End Tenancy the cause of having "an unreasonable number of occupants living in the unit". In response, a tenant can challenge the notice through '
            ),
            {
              text: t('faq:dispute resolution'),
              href: 'https://liv.rent/blog/2019/12/how-to-dispute-resolution/'
            }
          ]
        },
        {
          steps: [
            t(
              'faq:It is highly recommended that you include certain terms in your tenancy agreement, which includes'
            ),
            <ol key="Recommended terms list">
              <li>{t('faq:Limiting the number of additional occupants')}</li>
              <li>
                {t(
                  'faq:Allowing the rent to vary based on an agreed amount for each additional occupant -or- have all parties sign a new tenancy agreement.'
                )}
              </li>
            </ol>,
            {
              text: t('faq:Learn more'),
              href: 'https://www.bclaws.ca/civix/document/id/complete/statreg/477_2003'
            }
          ]
        }
      ]
    },
    {
      title: t('faq:How do I sign a rental contract with my tenant?'),
      path: 'how-to-sign-rental-contract',
      answers: [
        {
          steps: [
            t(
              'faq:You can sign your lease contract digitally through the liv.rent platform. NOTE: Contracts are based on the standard ',
              'You can sign your lease contract digitally through the liv.rent platform. NOTE: Contracts are based on the standard '
            ),
            {
              text: t('faq:BC Residential Tenancy Agreement'),
              href: 'https://liv.rent/blog/2019/09/residential-tenancy-agreement-explained/'
            }
          ]
        },
        {
          steps: [
            t(
              'faq:In just a few clicks, you can easily pull up all of your past, current or upcoming contracts. Additionally, if you need to make changes to your lease agreement, you can select from an array of pre-set addendums that are customizable to suit your needs.'
            )
          ]
        },
        {
          steps: [
            t('faq:Learn more about the different '),
            {
              text: t('faq:liv.rent features'),
              href: 'https://liv.rent/landlords'
            },
            t(
              'faq: for landlords and how it can help you manage your rental property. To get started, book a demo today.'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:When can landlords enter a rental unit?'),
      path: 'when-can-landlords-enter-unit',
      answers: [
        {
          steps: [
            t(
              'faq:In order to enter a rental unit, landlords must provide proper notice to the tenant which involves a written note that specifies the date, time and purpose for entering the property. This must be delivered in-person at least 24 hours prior to entry, and scheduled between 8AM - 9PM (unless you and your tenant have agreed on another time).'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:Purpose of entering rental unit',
              'The purpose of entering a rental unit includes one of the following:'
            ),
            <ul key="Entering rental unit list">
              <li>
                {t("faq:To inspect the property's condition once per month")}
              </li>
              <li>{t('faq:To complete repairs or maintenance')}</li>
              <li>
                {t('faq:To show the property to prospective tenants or buyers')}
              </li>
            </ul>
          ]
        },
        {
          steps: [
            t(
              'faq:For more circumstances on when landlords can enter a rental unit, visit '
            ),
            {
              text: 'gov.bc.ca.',
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/during-a-tenancy/landlord-s-access'
            }
          ]
        }
      ]
    },
    {
      title: t('faq:Does it cost to list on liv.rent?'),
      path: 'cost-to-list-livrent',
      answers: [
        {
          steps: [
            t(
              'faq:It is currently free to list as many properties as you manage on liv.rent with our Essentials plan. However, chances are if you have more than 3 properties, you will greatly benefit from signing up for either our '
            ),
            {
              text: t('faq:Growth or Business plan'),
              href: 'https://liv.rent/pricing'
            },
            '.'
          ]
        },
        {
          steps: [
            t('faq:Not ready to sign up? Book an '),
            {
              text: t('faq:informational session'),
              href: 'https://calendly.com/livrent/30min-educational-session?utm_source=livrent&utm_medium=landingpage&utm_campaign=landlords_main'
            },
            t(
              'faq: instead and speak with our team members about how liv.rent can help you market and manage your listings.'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:How can liv.rent help me find tenants?'),
      path: 'how-liv-rent-helps-find-tenants',
      answers: [
        {
          steps: [
            t('faq:You can list your property on liv.rent to leverage our '),
            {
              text: t('faq:suggested tenants'),
              href: 'https://liv.rent/blog/2019/08/suggested-tenants/'
            },
            t(
              'faq: feature, and instantly connect with thousands of pre-screened tenants. For each listing that is posted, liv.rent will automatically generate a list of prospective tenants based on their selected filters that match your property. '
            )
          ]
        },
        {
          steps: [
            t(
              'faq:Additionally, to help your property stand out, you can add professional photos and video tours to highlight specific features. >> Learn more on how tenants are '
            ),
            {
              text: t('faq:verified'),
              href: 'https://liv.rent/blog/2019/10/verification-process/'
            },
            t('faq: and '),
            {
              text: 'evaluated',
              href: 'https://liv.rent/blog/2019/10/trust-score-explained/'
            },
            t('faq: on liv.rent')
          ]
        },
        {
          steps: [
            t(
              'faq:You can also have your own Corporate Page to showcase your portfolio, and depending on your needs, liv.rent can help you create your own personal rental website to showcase your experience. '
            ),
            {
              text: t('faq:Click here'),
              href: 'mailto:info@liv.rent'
            },
            t('faq: to talk with one of our representatives today.')
          ]
        },
        {
          steps: [
            t(
              'faq:Since liv.rent enables multi-platform syndication, you can share your listing across different platforms including Craigslist, Kijiji, Zumper, Facebook Marketplace, and more. You can also be part of our "Sponsored Listing" to boost traffic to your rental listing. '
            ),
            {
              text: t('faq:Learn more'),
              href: 'https://liv.rent/pricing'
            },
            t('faq: about liv.rent features for landlords ')
          ]
        }
      ]
    },
    {
      title: t('faq:How do I check a potential tenant’s credit score?'),
      path: 'how-to-check-tenant-credit-score',
      answers: [
        {
          steps: [
            t(
              "faq:To check a potential tenant's credit score, you can use our "
            ),
            {
              text: 'Trust Score',
              href: 'https://liv.rent/blog/2019/10/trust-score-explained/'
            },
            t('faq: intelligent rating system.')
          ]
        },
        {
          steps: [
            t(
              "faq:This involves a standard credit check conducted by Equifax, which provides a comprehensive picture of the renter's financial credibility based on payment history, credit utilization, bankruptcy index, and so forth."
            )
          ]
        },
        {
          steps: [
            <Trans
              key="In addition to the credit check, liv.rent uses a powerful algorithm to combine a"
              i18nKey="faq:In addition to the credit check, liv.rent uses a powerful algorithm to combine a"
            >
              In addition to the credit check, liv.rent uses a powerful
              algorithm to combine a renter&apos;s financial record with public
              and submitted data to assign a{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/trust-score-explained/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Trust Score
              </ItemLink>{' '}
              that allows you to evaluate a renter&apos;s reliability and
              credibility.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t(
        'faq:How does the Trust Score compare to other credit check reports?'
      ),
      path: 'how-does-trust-score-compare',
      answers: [
        {
          steps: [
            t('faq:Unlike other credit check report, '),
            {
              text: 'Trust Score',
              href: 'https://liv.rent/blog/2019/10/trust-score-explained/'
            },
            t(
              'faq: allows for a more comprehensive picture of potential tenants. Using AI and a powerful algorithm, this intelligent rating system takes into account a renters financial record as well as public and submitted data (e.g. LinkedIn Profile) which then assigns a Trust Score to every renter.'
            )
          ]
        },
        {
          steps: [
            t(
              "faq:This offers landlords a more secured and accurate measure of a potential tenant's suitability and simplifies the tenant selection process without having to manually check yourself. "
            ),
            {
              text: t('faq:Click here'),
              href: 'https://liv.rent/blog/2019/10/trust-score-explained/'
            },
            t('faq: to learn more about Trust Score ')
          ]
        }
      ]
    },
    {
      title: t('faq:How do I collect rent from my tenants?'),
      path: 'how-to-collect-rent',
      answers: [
        {
          steps: [
            t(
              'faq:There are two ways to use liv.rent to collect rent payments from your tenants.'
            )
          ]
        },
        {
          heading: t('faq:Option 1'),
          steps: [
            <Trans
              key="If your tenant is paying by Visa, Mastercard, or American Express"
              i18nKey="faq:If your tenant is paying by Visa, Mastercard, or American Express"
            >
              If your tenant is paying by Visa, Mastercard, or American Express,
              then you can accept payments through{' '}
              <ItemLink
                href="https://stripe.com/en-ca"
                rel="noopener noreferrer"
                target="_blank"
              >
                Stripe
              </ItemLink>
              . All you need to do is connect your Stripe account with liv.rent
              and once your tenant makes a payment, it will be deposited into
              your Stripe account, which pays it out to your bank account.
            </Trans>
          ]
        },
        {
          heading: t('faq:Option 2'),
          steps: [
            t(
              'faq:If your tenant chooses to pay by UnionPay or bitcoin, then you can set up a direct deposit by putting your bank account information in your liv.rent profile.'
            )
          ]
        }
      ]
    },
    {
      title: t(
        'faq:Do I have to pay any service fees if I collect rent online using liv.rent? '
      ),
      path: 'service-fees-livrent',
      answers: [
        {
          steps: [
            t(
              'faq:If you’re accepting payments with Stripe, then the service fee that Stripe charges for each transaction will be passed on to your tenant. However, landlords may encounter additional charges from Stripe, so always check your Stripe account to see what fees have been applied.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:For tenants paying with UnionPay or bitcoin, as a landlord, you will have to pay a flat service fee of $3 CAD per transaction.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:These fees will be labelled as “Payout fee” in your payout details page.'
            )
          ]
        }
      ]
    },
    {
      title: t(
        'faq:If my renter underpaid, how do I receive the unpaid amount?'
      ),
      path: 'how-to-receive-unpaid-rent',
      answers: [
        {
          steps: [
            <Trans
              key="With the new Pay by Bitcoin feature underpaid"
              i18nKey="faq:With the new Pay by Bitcoin feature underpaid"
            >
              With the new{' '}
              <ItemLink
                href="https://liv.rent/blog/2021/05/how-to-pay-for-rent-with-bitcoin/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Pay by Bitcoin feature
              </ItemLink>
              , there may be extremely rare circumstances where due to exchange
              rate fluctuations, a renter may have underpaid for their rent.
            </Trans>
          ]
        },
        {
          steps: [
            t(
              'faq:In case of an underpayment, liv.rent will send a supplementary payment request to the renter stating the outstanding balance. Once the renter has paid the remaining balance and liv.rent has confirmed that the underpayment was corrected, the landlord will receive the full payment.'
            )
          ]
        },
        {
          steps: [
            <Trans
              key="For more information, you can refer to 410f"
              i18nKey="faq:For more information, you can refer to 410f"
            >
              For more information, you can refer to
              <ItemLink
                href="https://liv.rent/terms"
                rel="noopener noreferrer"
                target="_blank"
              >
                Section 4.10(f) of our Terms and Conditions
              </ItemLink>
              .
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:If my renter overpaid, how do I refund their money?'),
      path: 'how-to-refund-rent',
      answers: [
        {
          steps: [
            <Trans
              key="With the new Pay by Bitcoin feature overpaid"
              i18nKey="faq:With the new Pay by Bitcoin feature overpaid"
            >
              With the new{' '}
              <ItemLink
                href="https://liv.rent/blog/2021/05/how-to-pay-for-rent-with-bitcoin/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Pay by Bitcoin feature
              </ItemLink>
              , there may be extremely rare circumstances where due to exchange
              rate fluctuations, a renter may have overpaid for their rent.
            </Trans>
          ]
        },
        {
          steps: [
            t(
              'faq:In case of an overpayment, landlords will need to contact their renters directly and provide a refund or credit based on an agreed method. liv.rent will not take part in this refund process and any overpaid funds are directly transferred to the landlord.'
            )
          ]
        },
        {
          steps: [
            <Trans
              key="For more information, you can refer to 410g"
              i18nKey="faq:For more information, you can refer to 410g"
            >
              For more information, you can refer to
              <ItemLink
                href="https://liv.rent/terms"
                rel="noopener noreferrer"
                target="_blank"
              >
                Section 4.10(g) of our Terms and Conditions
              </ItemLink>
              .
            </Trans>
          ]
        }
      ]
    },
    {
      title: t(
        'faq:Under what circumstances is liv.rent responsible for refunds?'
      ),
      path: 'when-is-livrent-responsible-for-refunds',
      answers: [
        {
          steps: [
            <Trans
              key="For rare cases of invalid or cancelled payments, liv.rent may"
              i18nKey="faq:For rare cases of invalid or cancelled payments, liv.rent may"
            >
              For rare cases of invalid or cancelled payments, liv.rent may
              issue a refund. Please view{' '}
              <ItemLink
                href="https://liv.rent/terms"
                rel="noopener noreferrer"
                target="_blank"
              >
                Sections 4.10(h) and 4.10(d) of our Terms and Conditions
              </ItemLink>{' '}
              for the applicable refund situations.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="If the situation applies, please ask your renter to submit a"
              i18nKey="faq:If the situation applies, please ask your renter to submit a"
            >
              If the situation applies, please ask your renter to submit a
              refund request to{' '}
              <ItemLink
                href="mailto:support@liv.rent"
                rel="noopener noreferrer"
                target="_blank"
              >
                support@liv.rent
              </ItemLink>{' '}
              — liv.rent will then review the request and will issue a refund if
              the request is deemed valid.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t(
        'faq:Since renters can now pay with Bitcoin, can I receive rent in Bitcoin?'
      ),
      path: 'can-i-receive-rent-bitcoin',
      answers: [
        {
          steps: [
            <Trans
              key="Currently, landlords will not receive payments in bitcoin. When"
              i18nKey="faq:Currently, landlords will not receive payments in bitcoin. When"
            >
              Currently, landlords will not receive payments in bitcoin. When
              the renter pays with bitcoin, it is transferred and deposited by
              liv.rent in Canadian dollars into the landlord’s bank account.
              Learn more about{' '}
              <ItemLink
                href="https://liv.rent/blog/2021/05/how-to-pay-for-rent-with-bitcoin/"
                rel="noopener noreferrer"
                target="_blank"
              >
                bitcoin rent payment
              </ItemLink>{' '}
              on liv.rent by checking out our blog.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:Do I receive rent payments right away?'),
      path: 'when-do-i-receive-rent-payments',
      answers: [
        {
          steps: [
            <Trans
              key="If you’re accepting payments with Stripe, then you will receive"
              i18nKey="faq:If you’re accepting payments with Stripe, then you will receive"
            >
              If you’re accepting payments with Stripe, then you will receive
              your rent based on your account’s payout schedule. In most cases,
              it takes seven calendar days for rent payments to be received.
              Learn more about receiving payments through{' '}
              <ItemLink
                href="https://stripe.com/docs/payouts#payout-schedule"
                rel="noopener noreferrer"
                target="_blank"
              >
                Stripe
              </ItemLink>
              .
            </Trans>
          ]
        },
        {
          steps: [
            t(
              'faq:Payments made with UnionPay will typically take three to five business days.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:For payments made with bitcoin, the processing time will vary. Please check your dashboard for the payout schedule. liv.rent’s current payout schedule for landlords is every Tuesday.'
            ),
            <ImageWrapper key="ll-image-1">
              <Image
                alt="liv.rent landlord dashboard payout schedule"
                width={480}
                height={321}
                placeholder="blur"
                src={landlordTransactionImage}
              />
            </ImageWrapper>
          ]
        }
      ]
    },
    {
      title: t('faq:Why is there a payout fee?'),
      path: 'why-is-there-payout-fee',
      answers: [
        {
          steps: [
            t(
              'faq:The payout fee is collected to cover the costs of facilitating the transaction and any additional administrative costs.'
            )
          ]
        }
      ]
    }
  ]

  return <FaqAccordion data={LANDLORD_TIPS} />
}
export default Landlords
